/**
 * Reject a promise if it doesn't settle within `ms`.
 *
 * This bounds awaits that can otherwise hang forever. The motivating case is a
 * Firestore write issued after the JS SDK has wedged itself (the
 * `INTERNAL ASSERTION FAILED: Unexpected state` bug): the returned promise
 * never settles, which left the post-session note "Generating…" button spinning
 * indefinitely with no error and no recovery.
 *
 * Note: this does NOT cancel the underlying work — a Firestore write raced out
 * here may still eventually land. It only stops the caller from awaiting it
 * forever, so the UI can fail loudly and offer a retry instead of dead-locking.
 * For cancellable work (fetch), prefer an AbortController.
 */
export class TimeoutError extends Error {
  constructor(message = 'Operation timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  message?: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() =>
    clearTimeout(timer),
  ) as Promise<T>;
}
